import PageLoader from "@/components/loader/PageLoader";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { AxiosError } from "axios";
import { format } from "date-fns";
import { FormEvent, useEffect, useState, useCallback } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface Category {
  id: number;
  name: string;
}

interface PromotionData {
  name: string;
  description: string;
  status: string;
  promotionType: string;
  promotionAreaType: string;
  discountType: string;
  branch: any;
  startDate: Date | null;
  endDate: Date | null;
  dollarAmountOff: number;
  percentageOff: number;
  dollarAmountToBeReached: number;
  productAmountToBeReached: number;
  eligibilityCategories: Array<number>;
  eligibiliyItems: Array<number>;
  discountCategories: Array<number>;
  discountItems: Array<number>;
  code: string;
  maxUses: number;
  selectedDiscountItem: number;
  noMinimumRequired: boolean;
  automaticApply: boolean;
}

interface Item {
  id: number;
  name: string;
  category_id: number;
}

const AddPromotion = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [eligiblityItems, setEligiblityItems] = useState<Item[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [discountItemCategories, setDiscountItemCategories] = useState<
    Category[]
  >([]);
  const [selectedDiscountItemCategory, setSelectedDiscountItemCategory] =
    useState<string>("");
  const [discountItems, setDiscountItems] = useState<Item[]>([]);
  const [formData, setFormData] = useState<PromotionData>({
    name: "",
    description: "",
    status: "",
    promotionType: "",
    promotionAreaType: "",
    discountType: "",
    branch: "",
    startDate: null,
    endDate: null,
    dollarAmountToBeReached: 0,
    productAmountToBeReached: 0,
    dollarAmountOff: 0,
    percentageOff: 0,
    eligibilityCategories: [],
    eligibiliyItems: [],
    discountCategories: [],
    discountItems: [],
    code: "",
    maxUses: 1,
    selectedDiscountItem: 0,
    noMinimumRequired: false,
    automaticApply: false,
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedEligibilityCategory, setSelectedEligibilityCategory] =
    useState<string>("");
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const codeGenerationAxios = useAxiosInstance()[0]; // Only get the axios instance, ignore loading states
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const codeVerificationAxios = useAxiosInstance()[0];
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [isCodeAvailable, setIsCodeAvailable] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (!loading && axiosInstance && user?.selectedBranch) {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
            import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION
          }`;

          const response = await axiosInstance.get(
            `${apiUrl}?branch=${user.selectedBranch.id}`,
          );
          setCategories(response.data.results);
          setDiscountItemCategories(response.data.results);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [axiosInstance, loading, user?.selectedBranch]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        if (!loading && axiosInstance && selectedDiscountItemCategory) {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
            import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION
          }`;

          const response = await axiosInstance.get(
            `${apiUrl}?branch=${user?.selectedBranch?.id}&category=${selectedDiscountItemCategory}`,
          );
          setDiscountItems(response.data.results);
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };

    if (selectedDiscountItemCategory) {
      fetchItems();
    } else {
      setItems([]);
    }
  }, [
    axiosInstance,
    loading,
    selectedDiscountItemCategory,
    user?.selectedBranch?.id,
  ]);

  useEffect(() => {
    const apiUrl =
      import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION ?? "";
    const fetchItems = async () => {
      if (!selectedEligibilityCategory) return;
      try {
        if (!loading && axiosInstance) {
          const response = await axiosInstance.get(
            `${apiUrl}?branch=${user?.selectedBranch?.id}&category=${selectedEligibilityCategory}`,
          );
          console.log(response);
          setEligiblityItems(response.data.results);
        }
      } catch (error) {
        console.error("Error fetching discount item categories:", error);
      }
    };
    fetchItems();
  }, [
    axiosInstance,
    loading,
    selectedEligibilityCategory,
    user?.selectedBranch?.id,
  ]);

  const generateDiscountCode = async () => {
    setIsCodeVerified(false);
    try {
      setIsGeneratingCode(true);
      if (codeGenerationAxios) {
        const res = await codeGenerationAxios.get(
          `/discount/promotion/code-check?code=&branch=${user?.selectedBranch?.id}&`,
        );
        setFormData((prev) => ({
          ...prev,
          code: res.data.code,
        }));
      }
    } catch (error: any) {
      toast.error(error.response.data.error || "Error checking discount code");
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const discountCodeCheck = useCallback(async () => {
    try {
      setIsVerifyingCode(true);
      const res = await codeVerificationAxios.get(
        `/discount/promotion/code-check?code=${formData.code}&branch=${user?.selectedBranch?.id}`,
      );
      setIsCodeVerified(true);
      return true;
    } catch (error: any) {
      return false;
    } finally {
      setIsVerifyingCode(false);
    }
  }, [codeVerificationAxios, user?.selectedBranch?.id, formData.code]);

  useEffect(() => {
    const debouncedCodeCheck = setTimeout(() => {
      const checkCode = async () => {
        if (formData.code) {
          const isCodeAvailableNow = await discountCodeCheck();
          setIsCodeAvailable(isCodeAvailableNow);
        }
      };
      checkCode();
    }, 2000);
    return () => clearTimeout(debouncedCodeCheck);
  }, [formData.code, discountCodeCheck]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isCodeVerified) {
      const isCodeVerifiedNow = await discountCodeCheck();
      if (!isCodeVerifiedNow) {
        toast.error("Discount code is not available");
        return;
      }
    }
    if (loading || !axiosInstance) return;
    const selectedBranch = user?.selectedBranch?.id;
    const promotionData = {
      name: formData.name,
      description: formData.description,
      status: formData.status,
      promotion_type: formData.promotionType,
      promotion_area_type: formData.promotionAreaType,
      discount_type: formData.discountType,
      branch: selectedBranch,
      start_date: formData.startDate
        ? format(formData.startDate, "yyyy-MM-dd")
        : null,
      end_date: formData.endDate
        ? format(formData.endDate, "yyyy-MM-dd")
        : null,
      dollar_amount_to_be_reached: formData.dollarAmountToBeReached,
      product_amount_to_be_reached: formData.productAmountToBeReached,
      dollar_amount_off: formData.dollarAmountOff,
      percentage_off: formData.percentageOff,
      max_uses: formData.maxUses,
      code: formData.code,
      eligibility_categories: formData.eligibilityCategories,
      eligibility_items: formData.eligibiliyItems,
      discount_categories: formData.discountCategories,
      discount_items: formData.discountItems,
      automatic_apply: formData.automaticApply,
    };

    try {
      const response = await axiosInstance.post(
        "/discount/promotion/",
        promotionData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (response.status === 201) {
        const successMessage = t("back.management.menu.promotion.successadd");
        toast.success(successMessage);
        navigate("/promotions");
      }
    } catch (error) {
      console.error("Error adding promotion:", error);
      if (error instanceof Error) {
        if (error.name === "AxiosError") {
          const axiosError = error as AxiosError;
          if (axiosError.response) {
            const { status, data } = axiosError.response;
            if (status === 400) {
              // Bad Request error
              const errorData = data as { description?: string };
              const errorMessage = errorData.description || "Bad Request";
              toast.error(errorMessage);
            } else {
              toast.error(t("back.management.menu.promotion.failadd"));
            }
          } else {
            toast.error(t("back.management.menu.promotion.failadd"));
          }
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error(t("back.management.menu.promotion.failadd"));
      }
    }
  };

  const promotionTypeOptions = [
    {
      label: t("back.management.menu.promotion.productOrderDiscount"),
      value: "ONE_ITEM_DISCOUNT",
    },
    {
      label: t("back.management.menu.promotion.buyXGetYDiscount"),
      value: "BUY_X_GET_Y_DISCOUNT",
    },
    {
      label: t("back.management.menu.promotion.entireOrderDiscount"),
      value: "ENTIRE_ORDER_DISCOUNT",
    },
  ];

  const handleInputChange = (field: keyof PromotionData, value: any) => {
    if (field === "startDate" && value) {
      setFormData((prev) => ({
        ...prev,
        endDate: null,
      }));
    }
    if (field === "percentageOff" && value < 0) {
      toast.error("Percentage off cannot be less than 0");
      setFormData((prev) => ({
        ...prev,
        percentageOff: 0,
      }));
    }
    if (field === "dollarAmountOff" && value < 0) {
      toast.error("Dollar amount off cannot be less than 0");
      setFormData((prev) => ({
        ...prev,
        dollarAmountOff: 0,
      }));
    }
    if (field === "code") {
      setIsCodeVerified(false);
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderDynamicRow = (type: string) => {
    switch (type) {
      case "ONE_ITEM_DISCOUNT":
        return (
          <>
            <div className="mb-2 w-full space-x-2 px-2">
              <input
                type="checkbox"
                checked={formData.noMinimumRequired}
                onChange={(e) =>
                  handleInputChange("noMinimumRequired", e.target.checked)
                }
              />
              <label className="mb-2 text-sm font-bold text-gray-700">
                No minimum required
              </label>
            </div>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                {t("back.management.menu.promotion.cartamount")}
              </label>
              <input
                type="number"
                step="0.01"
                placeholder={t(
                  "back.management.menu.promotion.entercartamount",
                )}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring disabled:opacity-30"
                value={formData.dollarAmountToBeReached}
                disabled={formData.noMinimumRequired}
                onChange={(e) => {
                  handleInputChange("dollarAmountToBeReached", e.target.value);
                  handleInputChange("productAmountToBeReached", 0);
                }}
                min="0"
              />
            </div>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                {t("back.management.menu.promotion.itemCountAmount")}
              </label>
              <input
                type="number"
                step="1"
                placeholder={t(
                  "back.management.menu.promotion.enterItemCountAmount",
                )}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring disabled:opacity-30"
                value={formData.productAmountToBeReached}
                disabled={formData.noMinimumRequired}
                onChange={(e) => {
                  handleInputChange("productAmountToBeReached", e.target.value);
                  handleInputChange("dollarAmountToBeReached", 0);
                }}
                min="0"
              />
            </div>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                {t("back.management.menu.promotion.discounttype")}
                <span className="text-red-500">*</span>
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                required
                value={formData.discountType}
                onChange={(e) =>
                  handleInputChange("discountType", e.target.value)
                }
              >
                <option value="">
                  {t("back.management.menu.promotion.selectdiscounttype")}
                </option>
                <option value="Percentage">
                  {t("back.management.menu.promotion.percentageoff")}
                </option>
                <option value="Fixed Amount">
                  {t("back.management.menu.promotion.amountoff")}
                </option>
              </select>
            </div>
            {formData.discountType && (
              <>
                <div className="mb-4 w-full px-2">
                  <label className="mb-2 block text-sm font-bold text-gray-700">
                    {t("back.management.menu.promotion.discountItemCategory")}
                  </label>
                  <select
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                    value={formData.discountCategories[0]}
                    onChange={(e) => {
                      const selectedCategory = discountItemCategories.find(
                        (category) => category.id === parseInt(e.target.value),
                      );
                      if (selectedCategory) {
                        setSelectedDiscountItemCategory(selectedCategory.name);
                        handleInputChange("discountCategories", [
                          selectedCategory.id,
                        ]);
                      }
                    }}
                  >
                    <option value="">
                      {t(
                        "back.management.menu.promotion.selectDiscountItemCategory",
                      )}
                    </option>
                    {discountItemCategories.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4 w-full px-2 md:w-1/2">
                  <label className="mb-2 block text-sm font-bold text-gray-700">
                    {t("back.management.menu.promotion.discountItem")}
                  </label>
                  <select
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                    value={formData.discountItems[0]}
                    onChange={(e) =>
                      handleInputChange("discountItems", [
                        parseInt(e.target.value),
                      ])
                    }
                  >
                    <option value="">
                      {t("back.management.menu.promotion.selectDiscountItem")}
                    </option>
                    {discountItems.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4 w-full px-2 md:w-1/2">
                  {formData.discountType === "Percentage" && (
                    <div>
                      <label className="mb-2 block text-sm font-bold text-gray-700">
                        {t("back.management.menu.promotion.discountpercentage")}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={t(
                          "back.management.menu.promotion.enterdiscountpercentage",
                        )}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                        value={formData.percentageOff}
                        onChange={(e) => {
                          if (Number(e.target.value) < 0) {
                            e.target.value = "0";
                            return;
                          }
                          handleInputChange("percentageOff", e.target.value);
                        }}
                        min="0"
                      />
                    </div>
                  )}

                  {formData.discountType === "Fixed Amount" && (
                    <div>
                      <label className="mb-2 block text-sm font-bold text-gray-700">
                        {t("back.management.menu.promotion.discountamount")}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={t(
                          "back.management.menu.promotion.enterdiscountamount",
                        )}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                        value={formData.dollarAmountOff}
                        onChange={(e) => {
                          if (Number(e.target.value) < 0) {
                            e.target.value = "0";
                            return;
                          }
                          handleInputChange("dollarAmountOff", e.target.value);
                        }}
                        min="0"
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        );
      case "BUY_X_GET_Y_DISCOUNT":
        return (
          <>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Eligibility Category
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                value={formData.eligibilityCategories[0]}
                onChange={(e) => {
                  const selectedCategory = categories.find(
                    (category) => category.id === parseInt(e.target.value),
                  );
                  if (selectedCategory) {
                    setSelectedEligibilityCategory(selectedCategory.name);
                    handleInputChange("eligibilityCategories", [
                      selectedCategory.id,
                    ]);
                  }
                }}
              >
                <option value="">
                  {t("back.management.menu.promotion.selectcategory")}
                </option>
                {categories.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4 w-full px-2 md:w-1/2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Eligiblity Item
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                value={formData.eligibiliyItems[0]}
                onChange={(e) =>
                  handleInputChange("eligibiliyItems", [
                    parseInt(e.target.value),
                  ])
                }
              >
                <option value="">
                  {t("back.management.menu.promotion.selectDiscountItem")}
                </option>
                {eligiblityItems.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <label className="mb-2 block px-2 text-sm font-bold text-gray-700">
              Set either the item count or the dollar amount
            </label>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Required Amount
              </label>
              <input
                type="number"
                step="0.01"
                placeholder={t(
                  "back.management.menu.promotion.enterthreshholdamount",
                )}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                value={formData.dollarAmountToBeReached}
                onChange={(e) => {
                  handleInputChange("dollarAmountToBeReached", e.target.value);
                  handleInputChange("productAmountToBeReached", 0);
                }}
                min="0"
              />
            </div>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                Required Item Count
              </label>
              <input
                type="number"
                step="1"
                placeholder={t(
                  "back.management.menu.promotion.enterItemCountAmount",
                )}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                value={formData.productAmountToBeReached}
                onChange={(e) => {
                  handleInputChange("productAmountToBeReached", e.target.value);
                  handleInputChange("dollarAmountToBeReached", 0);
                }}
                min="0"
              />
            </div>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                {t("back.management.menu.promotion.discounttype")}
                <span className="text-red-500">*</span>
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                value={formData.discountType}
                required
                onChange={(e) =>
                  handleInputChange("discountType", e.target.value)
                }
              >
                <option value="">
                  {t("back.management.menu.promotion.selectdiscounttype")}
                </option>
                <option value="Percentage">
                  {t("back.management.menu.promotion.percentageoff")}
                </option>
                <option value="Fixed Amount">
                  {t("back.management.menu.promotion.amountoff")}
                </option>
              </select>
            </div>
            {formData.discountType && (
              <>
                <div className="mb-4 w-full px-2">
                  <label className="mb-2 block text-sm font-bold text-gray-700">
                    {t("back.management.menu.promotion.discountItemCategory")}
                  </label>
                  <select
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                    value={formData.discountCategories[0]}
                    onChange={(e) => {
                      const selectedCategory = discountItemCategories.find(
                        (category) => category.id === parseInt(e.target.value),
                      );
                      if (selectedCategory) {
                        setSelectedDiscountItemCategory(selectedCategory.name);
                        handleInputChange("discountCategories", [
                          selectedCategory.id,
                        ]);
                      }
                    }}
                  >
                    <option value="">
                      {t(
                        "back.management.menu.promotion.selectDiscountItemCategory",
                      )}
                    </option>
                    {discountItemCategories.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4 w-full px-2 md:w-1/2">
                  <label className="mb-2 block text-sm font-bold text-gray-700">
                    {t("back.management.menu.promotion.discountItem")}
                  </label>
                  <select
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                    value={formData.discountItems[0]}
                    onChange={(e) =>
                      handleInputChange("discountItems", [
                        parseInt(e.target.value),
                      ])
                    }
                  >
                    <option value="">
                      {t("back.management.menu.promotion.selectDiscountItem")}
                    </option>
                    {discountItems.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4 w-full px-2 md:w-1/2">
                  {formData.discountType === "Percentage" && (
                    <div>
                      <label className="mb-2 block text-sm font-bold text-gray-700">
                        {t("back.management.menu.promotion.discountpercentage")}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={t(
                          "back.management.menu.promotion.enterdiscountpercentage",
                        )}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                        value={formData.percentageOff}
                        onChange={(e) => {
                          if (Number(e.target.value) < 0) {
                            e.target.value = "0";
                            return;
                          }
                          handleInputChange("percentageOff", e.target.value);
                        }}
                        min="0"
                      />
                    </div>
                  )}

                  {formData.discountType === "Fixed Amount" && (
                    <div>
                      <label className="mb-2 block text-sm font-bold text-gray-700">
                        {t("back.management.menu.promotion.discountamount")}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={t(
                          "back.management.menu.promotion.enterdiscountamount",
                        )}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                        value={formData.dollarAmountOff}
                        onChange={(e) => {
                          if (Number(e.target.value) < 0) {
                            e.target.value = "0";
                            return;
                          }
                          handleInputChange("dollarAmountOff", e.target.value);
                        }}
                        min="0"
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        );
      case "ENTIRE_ORDER_DISCOUNT":
        return (
          <>
            <div className="mb-2 w-full space-x-2 px-2">
              <input
                type="checkbox"
                checked={formData.noMinimumRequired}
                onChange={(e) =>
                  handleInputChange("noMinimumRequired", e.target.checked)
                }
              />
              <label className="mb-2 text-sm font-bold text-gray-700">
                No minimum required
              </label>
            </div>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                {t("back.management.menu.promotion.cartamount")}
              </label>
              <input
                type="number"
                step="0.01"
                placeholder={t(
                  "back.management.menu.promotion.entercartamount",
                )}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring disabled:opacity-30"
                value={formData.dollarAmountToBeReached}
                disabled={formData.noMinimumRequired}
                onChange={(e) => {
                  handleInputChange("dollarAmountToBeReached", e.target.value);
                  handleInputChange("productAmountToBeReached", 0);
                }}
                min="0"
              />
            </div>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                {t("back.management.menu.promotion.itemCountAmount")}
              </label>
              <input
                type="number"
                step="1"
                placeholder={t(
                  "back.management.menu.promotion.enterItemCountAmount",
                )}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring disabled:opacity-30"
                value={formData.productAmountToBeReached}
                disabled={formData.noMinimumRequired}
                onChange={(e) => {
                  handleInputChange("productAmountToBeReached", e.target.value);
                  handleInputChange("dollarAmountToBeReached", 0);
                }}
                min="0"
              />
            </div>
            <div className="mb-4 w-full px-2">
              <label className="mb-2 block text-sm font-bold text-gray-700">
                {t("back.management.menu.promotion.discounttype")}
                <span className="text-red-500">*</span>
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                value={formData.discountType}
                required
                onChange={(e) =>
                  handleInputChange("discountType", e.target.value)
                }
              >
                <option value="">
                  {t("back.management.menu.promotion.selectdiscounttype")}
                </option>
                <option value="Percentage">
                  {t("back.management.menu.promotion.percentageoff")}
                </option>
                <option value="Fixed Amount">
                  {t("back.management.menu.promotion.amountoff")}
                </option>
              </select>
            </div>
            {formData.discountType && (
              <div className="mb-4 w-full px-2 md:w-1/2">
                {formData.discountType === "Percentage" && (
                  <div>
                    <label className="mb-2 block text-sm font-bold text-gray-700">
                      {t("back.management.menu.promotion.discountpercentage")}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder={t(
                        "back.management.menu.promotion.enterdiscountpercentage",
                      )}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                      value={formData.percentageOff}
                      onChange={(e) => {
                        if (Number(e.target.value) < 0) {
                          e.target.value = "0";
                          return;
                        }
                        handleInputChange("percentageOff", e.target.value);
                      }}
                      min="0"
                    />
                  </div>
                )}

                {formData.discountType === "Fixed Amount" && (
                  <div>
                    <label className="mb-2 block text-sm font-bold text-gray-700">
                      {t("back.management.menu.promotion.discountamount")}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder={t(
                        "back.management.menu.promotion.enterdiscountamount",
                      )}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                      value={formData.dollarAmountOff}
                      onChange={(e) => {
                        if (Number(e.target.value) < 0) {
                          e.target.value = "0";
                          return;
                        }
                        handleInputChange("dollarAmountOff", e.target.value);
                      }}
                      min="0"
                    />
                  </div>
                )}
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const statusOptions = [
    <option key="active" value="Active">
      {t("back.management.menu.category.status1")}
    </option>,
    <option key="inactivate" value="Inactive">
      {t("back.management.menu.category.status2")}
    </option>,
    <option key="draft" value="Draft">
      {t("back.management.menu.category.status4")}
    </option>,
  ];

  const promotionAreaTypeOptions = [
    <option key="External" value="External">
      {t("back.management.menu.promotion.external")}
    </option>,
    <option key="Internal" value="Internal">
      {t("back.management.menu.promotion.internal")}
    </option>,
  ];

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-xl font-bold">
          {t("back.management.menu.promotion.addpromotion")}
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.promotion.generalinfo")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:mb-0 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="name"
                >
                  {t("back.management.menu.promotion.promotionname")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder="Enter promotion type"
                  value={formData.name}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    handleInputChange("name", inputValue);
                  }}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
              <div className="w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="promotionAreaType"
                >
                  {t("back.management.menu.promotion.promotionAreaType")}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.promotionAreaType}
                  onChange={(e) =>
                    handleInputChange("promotionAreaType", e.target.value)
                  }
                  className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                >
                  <option value="">
                    {t(
                      "back.management.menu.promotion.selectPromotionAreaType",
                    )}
                  </option>
                  {promotionAreaTypeOptions}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label
                className="mb-2 block text-sm font-bold text-gray-700"
                htmlFor="description"
              >
                {t("back.management.menu.category.description")}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                required
                placeholder="Enter promotion description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                className="block h-24 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
              />
            </div>
            <div className="mt-4 flex flex-col">
              <div className="mb-1 flex w-full items-center justify-between">
                <label className="mb-1 text-sm font-bold text-gray-700">
                  Discount Code
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={generateDiscountCode}
                    disabled={isGeneratingCode || isVerifyingCode}
                    className="flex items-center justify-center space-x-2 rounded-md px-2 text-sm text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isGeneratingCode && (
                      <ArrowPathIcon className="ml-2 h-4 w-4 animate-spin" />
                    )}
                    Generate Discount Code
                  </button>
                </div>
              </div>
              <div className="flex min-w-full max-w-full flex-col">
                <input
                  type="text"
                  id="code"
                  required
                  placeholder="Enter or Generate Discount Code"
                  value={formData.code}
                  onChange={(e) => handleInputChange("code", e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
                <div className="flex max-h-3 min-h-3 justify-end">
                  {formData.code.length > 0 && formData.code.length < 4 && (
                    <span className="w-full text-right text-sm text-red-500">
                      Code must be at least 4 characters
                    </span>
                  )}
                  {!isCodeAvailable && (
                    <span className="w-full text-right text-sm text-red-500">
                      Code is not available
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="ml-2 flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.automaticApply}
                onChange={(e) =>
                  handleInputChange("automaticApply", e.target.checked)
                }
              />
              <label className="text-sm font-bold text-gray-700">
                Apply Automatically
              </label>
            </div>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.promotion.promotiontype")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2">
                <label
                  className="mb-2 mt-2 block text-sm font-bold text-gray-700"
                  htmlFor="promotion_type"
                >
                  {t("back.management.menu.promotion.type")}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="promotion_type"
                  required
                  value={formData.promotionType}
                  onChange={(e) =>
                    handleInputChange("promotionType", e.target.value)
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                >
                  <option value="">{t("Select Promotion Type")}</option>
                  {promotionTypeOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid w-full grid-cols-1 md:grid-cols-2">
                <div className="mb-4 w-full px-2">
                  <label
                    className="mb-2 block text-sm font-bold text-gray-700"
                    htmlFor="startDate"
                  >
                    {t("Start Date")}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <ReactDatePicker
                      id="startDate"
                      required
                      selected={formData.startDate}
                      onChange={(date: Date | null) =>
                        handleInputChange("startDate", date)
                      }
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 pl-10 shadow-sm focus:outline-none focus:ring"
                      dateFormat="MM/dd/yyyy"
                      placeholderText={t("Select Start Date")}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        (e.target.readOnly = true)
                      }
                    />
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="text-gray-400"
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-4 w-full px-2">
                  <label
                    className="mb-2 block text-sm font-bold text-gray-700"
                    htmlFor="endDate"
                  >
                    {t("End Date")}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="group relative">
                    <ReactDatePicker
                      id="endDate"
                      required
                      selected={formData.endDate}
                      onChange={(date: Date | null) =>
                        handleInputChange("endDate", date)
                      }
                      disabled={!formData.startDate}
                      minDate={formData.startDate ?? undefined}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 pl-10 shadow-sm focus:outline-none focus:ring disabled:cursor-not-allowed disabled:opacity-30"
                      dateFormat="MM/dd/yyyy"
                      placeholderText={t("Select End Date")}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        (e.target.readOnly = true)
                      }
                    />
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="text-gray-400"
                      />
                    </div>
                    {!formData.startDate && (
                      <div className="absolute left-0 top-full mt-2 transform opacity-0 transition-all duration-200 ease-in-out group-hover:opacity-100">
                        <div className="rounded-md bg-gray-800 px-2 py-1 text-sm text-white">
                          {t("Add the start date first")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {renderDynamicRow(formData.promotionType)}
            </div>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.category.status")}
              <span className="text-red-500">*</span>
            </h2>
            <select
              value={formData.status}
              required
              onChange={(e) => handleInputChange("status", e.target.value)}
              className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
            >
              <option value="">
                {t("back.management.menu.category.selectstatus")}
              </option>
              {statusOptions}
            </select>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">Max uses</h2>
            <input
              type="number"
              step="1"
              value={formData.maxUses}
              onChange={(e) =>
                handleInputChange("maxUses", parseInt(e.target.value, 10))
              }
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
              min="1"
            />
          </div>
          <div className="mt-4 flex justify-end">
            <Link
              to="/promotions"
              className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.category.cancelbutton")}
            </Link>
            <button
              type="submit"
              className="rounded-md bg-red-500 px-4 py-2 text-white hover:bg-red-600"
            >
              {t("back.management.menu.promotion.confirmbutton")}
            </button>
          </div>
        </form>
      </div>
    </PageLoader>
  );
};

export default AddPromotion;
